import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { useAuth, AuthProvider } from './AuthContext';
import { getImageUrl } from './imageHelper';
import { 
  Package, CheckCircle, AlertCircle, 
  Clock, User, LogOut, X,
  AlertTriangle, ShoppingBag, Scan, Box, Eye, EyeOff
} from 'lucide-react';
import BoxAllocationModal from './BoxAllocationModal';
import LoginScreen from './LoginScreen';
import LoadingScreen from './LoadingScreen';

// ============================================
// NOTIFY DRIVERS UTILITY
// ============================================
const notifyDriversNewDelivery = async ({ order_number, delivery_zone, box_count = 1 }) => {
  try {
    console.log('🔔 Notifying drivers about order:', order_number);
    
    const { data, error } = await supabase.functions.invoke('notify-drivers', {
      body: {
        order_number,
        delivery_zone,
        box_count
      }
    });

    if (error) {
      console.error('❌ Error notifying drivers:', error);
      return { success: false, error };
    }

    console.log('✅ Drivers notified:', data);
    return { success: true, data };
    
  } catch (err) {
    console.error('❌ Failed to notify drivers:', err);
    return { success: false, error: err.message };
  }
};

// ============================================
// SCAN PICK NUMBER SCREEN
// ============================================
const ScanPickNumberScreen = ({ onOrderLoaded, onResumeCompletion, onLogout, packerName, resetKey, packerId }) => {
  const [pickNumber, setPickNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pickNumberInputRef = useRef(null);
  const submitTimeoutRef = useRef(null);
  const recentlyScannedPickNumbers = useRef(new Set()); // Track scanned pick numbers

  // Reset scanned pick numbers when component remounts or resetKey changes
  useEffect(() => {
    console.log('🔄 Resetting scanned pick numbers');
    recentlyScannedPickNumbers.current.clear();
  }, [resetKey]);

  // Keep input focused at all times for handheld scanner
  useEffect(() => {
    const keepFocused = () => {
      if (pickNumberInputRef.current && document.activeElement !== pickNumberInputRef.current) {
        pickNumberInputRef.current.focus();
        console.log('🎯 Re-focusing input field');
      }
    };

    // Initial focus
    keepFocused();
    
    // Check focus frequently
    const interval = setInterval(keepFocused, 100); // More frequent - every 100ms
    
    // Re-focus on any click anywhere
    const handleClick = () => {
      setTimeout(keepFocused, 10);
    };
    
    // Re-focus on any key press
    const handleKeyPress = (e) => {
      // If any key is pressed and input is not focused, focus it
      if (document.activeElement !== pickNumberInputRef.current) {
        keepFocused();
      }
    };
    
    // Re-focus when window regains focus
    const handleWindowFocus = () => {
      keepFocused();
    };
    
    // Re-focus on mouse movement (for when user moves mouse after being idle)
    const handleMouseMove = () => {
      if (document.activeElement !== pickNumberInputRef.current) {
        keepFocused();
      }
    };
    
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyPress);
    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      clearInterval(interval);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const loadOrder = async (pickNum) => {
    try {
      // Convert pick number to integer if it's numeric
      const searchPickNumber = parseInt(pickNum) || pickNum;
      
      console.log('🔍 Searching for pick number:', searchPickNumber, 'Type:', typeof searchPickNumber);
      
      const { data: pickingTask, error: taskError } = await supabase
        .from('picking_tasks')
        .select('id, pick_number, order_id, picker_id, completed_at, has_shortages, shortage_details, status')
        .eq('pick_number', searchPickNumber)
        .eq('status', 'completed')
        .single();

      console.log('📦 Picking task result:', pickingTask, 'Error:', taskError);

      if (taskError || !pickingTask) {
        setError(`Pick number not found or order not yet picked.`);
        setLoading(false);
        // Remove from scanned set since this failed
        recentlyScannedPickNumbers.current.delete(pickNum);
        return;
      }

      // CRITICAL: Check if this order already has an active or completed packing task
      const { data: existingPackingTasks, error: packingCheckError } = await supabase
        .from('packing_tasks')
        .select('id, status, packer_id, order_id')
        .eq('order_id', pickingTask.order_id)
        .in('status', ['in_progress', 'completed']);

      if (packingCheckError) {
        console.error('❌ Error checking existing packing tasks:', packingCheckError);
      }

      if (existingPackingTasks && existingPackingTasks.length > 0) {
        console.log('🔍 Order has existing packing task(s):', existingPackingTasks);
        console.log('🔍 Current packer ID:', packerId, 'Type:', typeof packerId);
        
        const completedTask = existingPackingTasks.find(t => t.status === 'completed');
        const activeTask = existingPackingTasks.find(t => t.status === 'in_progress');
        
        if (completedTask) {
          // Check if completed task belongs to current packer
          if (String(completedTask.packer_id) === String(packerId)) {
            // Check if box has been allocated and placement confirmed
            const { data: orderData } = await supabase
              .from('orders')
              .select('id, order_number, box_allocation_id, box_placement_confirmed')
              .eq('id', completedTask.order_id)
              .single();
            
            // Resume if: no box allocated OR box allocated but placement not confirmed
            if (orderData && (!orderData.box_allocation_id || !orderData.box_placement_confirmed)) {
              console.log('📦 Completed task needs box allocation or placement confirmation, resuming to completion screen');
              console.log('📦 box_allocation_id:', orderData.box_allocation_id, 'box_placement_confirmed:', orderData.box_placement_confirmed);
              // Resume to completion screen for box allocation/confirmation
              if (onResumeCompletion) {
                onResumeCompletion(orderData, completedTask);
              }
              setPickNumber('');
              setLoading(false);
              return;
            }
          }
          
          // Either not our task or already fully completed (box allocated and confirmed)
          setError(`This order has already been packed! Pick number ${searchPickNumber} was completed.`);
          setPickNumber('');
          setLoading(false);
          recentlyScannedPickNumbers.current.delete(pickNum);
          return;
        } else if (activeTask) {
          console.log('🔍 Active task packer_id:', activeTask.packer_id, 'Type:', typeof activeTask.packer_id);
          console.log('🔍 Match check:', activeTask.packer_id === packerId, 'Loose match:', activeTask.packer_id == packerId);
          
          // Check if the active task belongs to the current packer (compare as strings to be safe)
          if (String(activeTask.packer_id) === String(packerId)) {
            console.log('✅ Resuming own packing task:', activeTask.id);
            // Allow to continue - this packer is resuming their own task
          } else {
            setError(`This order is currently being packed by another packer!`);
            setPickNumber('');
            setLoading(false);
            recentlyScannedPickNumbers.current.delete(pickNum);
            return;
          }
        }
      }

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select(`
          id, 
          order_number, 
          status, 
          plastic_bags_count,
          user_id,
          delivery_address_id,
          delivery_zone,
          order_items (
            id, 
            product_id, 
            quantity, 
            picked_quantity, 
            price,
            products (
              id, 
              name, 
              image, 
              barcode
            )
          )
        `)
        .eq('id', pickingTask.order_id)
        .single();

      console.log('📦 Order result:', order, 'Error:', orderError);

      if (orderError || !order) {
        console.error('❌ Order error:', orderError);
        setError(`Failed to load order.`);
        setLoading(false);
        // Remove from scanned set since this failed
        recentlyScannedPickNumbers.current.delete(pickNum);
        return;
      }

      // Fetch customer details separately
      let customer = null;
      if (order.user_id) {
        const { data: userData } = await supabase
          .from('users')
          .select('name, phone')
          .eq('id', order.user_id)
          .single();
        customer = userData;
      }

      // Add customer to order
      order.customer = customer;

      let shortages = [];
      if (pickingTask.has_shortages) {
        const { data: shortageData } = await supabase
          .from('picking_shortages')
          .select(`
            *,
            order_items (
              id,
              product_id,
              quantity,
              products (
                id,
                name,
                barcode
              )
            )
          `)
          .eq('picking_task_id', pickingTask.id);
        shortages = shortageData || [];
      }

      // Only keep in scanned set if successful - will be cleared when returning to scan screen
      onOrderLoaded({ ...pickingTask, order, shortages });
    } catch (err) {
      setError(`Failed to load order: ${err.message}`);
      setLoading(false);
      // Remove from scanned set since this failed
      recentlyScannedPickNumbers.current.delete(pickNum);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setLoading(true);
    setError('');

    console.log('📝 handleSubmit called with pickNumber:', pickNumber);

    const trimmedPickNumber = pickNumber.trim();
    if (!trimmedPickNumber) {
      setError('Please enter a pick number');
      setLoading(false);
      return;
    }

    // Check for duplicate pick number scan
    if (recentlyScannedPickNumbers.current.has(trimmedPickNumber)) {
      console.log('⚠️ Duplicate scan detected for pick number:', trimmedPickNumber);
      setError(`Pick number ${trimmedPickNumber} was already scanned in this session! Please scan a different order.`);
      setPickNumber('');
      setLoading(false);
      return;
    }

    // Add to recently scanned BEFORE attempting to load
    // Will be removed if load fails
    recentlyScannedPickNumbers.current.add(trimmedPickNumber);
    console.log('📝 Added to scanned set:', trimmedPickNumber, 'Set size:', recentlyScannedPickNumbers.current.size);

    await loadOrder(trimmedPickNumber);
    
    // Clear input after submission
    setPickNumber('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-purple-100">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
              <Package size={24} className="text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Packer Portal</h1>
              <p className="text-sm text-gray-600">{packerName}</p>
            </div>
          </div>
          <button onClick={onLogout} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <LogOut size={20} />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto mt-8">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="bg-purple-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <Scan size={40} className="text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Scan Pick Number</h2>
            <p className="text-gray-600">Use your handheld scanner or enter the pick number manually</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Scan className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                ref={pickNumberInputRef}
                type="text"
                inputMode="numeric"
                value={pickNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log('⌨️ Input changed:', value, 'Length:', value.length);
                  setPickNumber(value);
                }}
                onKeyDown={(e) => {
                  console.log('🔑 Key pressed:', e.key, 'Code:', e.code);
                  if (e.key === 'Enter') {
                    console.log('✅ Enter key detected - submitting form');
                    handleSubmit(e);
                  }
                }}
                onFocus={() => console.log('✅ Input focused')}
                onBlur={() => console.log('⚠️ Input lost focus')}
                placeholder="Scan or enter pick number..."
                className="w-full pl-10 pr-4 py-4 border-2 border-purple-500 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-600 bg-purple-50 text-lg"
                autoFocus
                autoComplete="off"
              />
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-purple-600">
              <Scan size={16} />
              <span className="font-medium">Scanner Ready - Point and Scan</span>
            </div>

            <button type="submit" disabled={loading || !pickNumber.trim()} className="w-full bg-purple-600 text-white font-bold py-4 rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400">
              {loading ? 'Loading Order...' : 'Load Order'}
            </button>
          </form>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// PACKING SCREEN
// ============================================
const PackingScreen = ({ pickingTask, onComplete, packerId }) => {
  const [order, setOrder] = useState(pickingTask.order);
  const [shortages] = useState(pickingTask.shortages || []);
  const [barcode, setBarcode] = useState('');
  const [verifiedItems, setVerifiedItems] = useState({});
  const [packingTaskId, setPackingTaskId] = useState(null);
  const [showExtraItemModal, setShowExtraItemModal] = useState(false);
  const [scannedExtraBarcode, setScannedExtraBarcode] = useState('');
  const [extraItems, setExtraItems] = useState([]);
  const [completing, setCompleting] = useState(false);
  const barcodeInputRef = useRef(null);
  const submitTimeoutRef = useRef(null);

  useEffect(() => {
    const initPackingTask = async () => {
      // First, check if there's an existing in_progress packing task for this order by this packer
      console.log('🔍 Checking for existing packing task for order:', order.id, 'packer:', packerId);
      
      const { data: existingTask, error: fetchError } = await supabase
        .from('packing_tasks')
        .select('*')
        .eq('order_id', order.id)
        .eq('packer_id', packerId)
        .eq('status', 'in_progress')
        .single();
      
      if (existingTask && !fetchError) {
        console.log('✅ Found existing packing task, resuming:', existingTask.id);
        setPackingTaskId(existingTask.id);
        
        // Restore verified items if any were saved
        if (existingTask.verified_items) {
          console.log('📦 Restoring verified items:', existingTask.verified_items);
          setVerifiedItems(existingTask.verified_items);
        }
        return;
      }
      
      // No existing task, create a new one
      console.log('🏗️ Creating packing task for order:', order.id, 'packer:', packerId, 'picking_task:', pickingTask.id);
      
      const { data: task, error } = await supabase
        .from('packing_tasks')
        .insert({ 
          order_id: order.id, 
          packer_id: packerId,
          picking_task_id: pickingTask.id,
          status: 'in_progress', 
          started_at: new Date().toISOString() 
        })
        .select()
        .single();
      
      if (error) {
        console.error('❌ Error creating packing task:', error);
        alert(`Failed to create packing task: ${error.message}`);
        return;
      }
      
      if (task) {
        console.log('✅ Packing task created successfully:', task.id);
        setPackingTaskId(task.id);
      } else {
        console.error('❌ No task returned from insert');
        alert('Failed to create packing task - no data returned');
      }
    };
    initPackingTask();
  }, [order.id, packerId, pickingTask.id]);

  useEffect(() => {
    const keepFocused = () => {
      if (barcodeInputRef.current && document.activeElement !== barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    };
    keepFocused();
    const interval = setInterval(keepFocused, 100);
    const handleClick = () => setTimeout(keepFocused, 10);
    document.addEventListener('click', handleClick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Get shortage record for an item from picking_shortages table
  const getShortageForItem = (item) => {
    return shortages.find(s => s.order_item_id === item.id);
  };

  // Check if item has full shortage (picked_quantity = 0 in shortages table)
  const isFullShortage = (item) => {
    const shortage = getShortageForItem(item);
    return shortage && shortage.picked_quantity === 0;
  };

  // Get actual picked quantity - check shortages table first
  const getActualPickedQuantity = (item) => {
    const shortage = getShortageForItem(item);
    if (shortage) {
      // Shortage record exists - use picked_quantity from shortages table
      return shortage.picked_quantity;
    }
    // No shortage record = fully picked
    return item.quantity;
  };

  const getItemStatus = (item) => {
    const expectedQuantity = getActualPickedQuantity(item);
    const hasShortage = expectedQuantity < item.quantity;
    const verifiedQuantity = verifiedItems[item.id] || 0;
    return { hasShortage, expectedQuantity, verifiedQuantity, orderedQuantity: item.quantity, isFullShortage: isFullShortage(item) };
  };

  // Items to pack = items NOT in shortages table OR with picked_quantity > 0 in shortages
  const itemsToPack = order?.order_items?.filter(item => !isFullShortage(item)) || [];

  // Full shortage items (picked_quantity = 0 in shortages) - shown as info only
  const shortageItems = order?.order_items?.filter(item => isFullShortage(item)) || [];

  const handleBarcodeSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!barcode.trim()) return;

    console.log('📦 Barcode submitted:', barcode);
    const matchedItem = itemsToPack.find(item => item.products.barcode === barcode);
    if (matchedItem) {
      const itemStatus = getItemStatus(matchedItem);
      console.log('✅ Matched item:', matchedItem.products.name, 'Status:', itemStatus);
      if (itemStatus.verifiedQuantity < itemStatus.expectedQuantity) {
        setVerifiedItems(prev => ({ ...prev, [matchedItem.id]: (prev[matchedItem.id] || 0) + 1 }));
        setBarcode('');
      } else {
        alert(`All items for ${matchedItem.products.name} have been verified!`);
        setBarcode('');
      }
    } else {
      console.log('⚠️ No matching item found for barcode:', barcode);
      setScannedExtraBarcode(barcode);
      setShowExtraItemModal(true);
    }
  };

  const handleAddExtraItem = async () => {
    const { data: product } = await supabase.from('products').select('id, name, barcode').eq('barcode', scannedExtraBarcode).single();
    if (product) {
      const existingExtra = extraItems.find(item => item.barcode === scannedExtraBarcode);
      if (existingExtra) {
        setExtraItems(prev => prev.map(item => item.barcode === scannedExtraBarcode ? { ...item, quantity: item.quantity + 1 } : item));
      } else {
        setExtraItems(prev => [...prev, { product_id: product.id, product_name: product.name, barcode: scannedExtraBarcode, quantity: 1 }]);
      }
      setShowExtraItemModal(false);
      setScannedExtraBarcode('');
      setBarcode('');
    }
  };

  const allItemsVerified = () => {
    // If no items to pack (all shortage), allow completion
    if (itemsToPack.length === 0) return true;
    
    return itemsToPack.every(item => {
      const itemStatus = getItemStatus(item);
      return itemStatus.verifiedQuantity >= itemStatus.expectedQuantity;
    });
  };

  const handleComplete = async () => {
    if (!allItemsVerified()) {
      alert('Please verify all items before completing packing!');
      return;
    }
    
    if (!packingTaskId) {
      console.error('❌ No packing task ID - cannot complete');
      alert('Error: Packing task was not created properly. Please reload and try again.');
      return;
    }
    
    if (completing) {
      console.log('⚠️ Already completing, please wait...');
      return;
    }

    console.log('🎯 Starting packing completion...');
    console.log('📦 Packing task ID:', packingTaskId);
    console.log('✅ All items verified:', allItemsVerified());
    
    setCompleting(true);
    
    try {
      const { error } = await supabase
        .from('packing_tasks')
        .update({ 
          status: 'completed', 
          completed_at: new Date().toISOString() 
        })
        .eq('id', packingTaskId);

      if (error) {
        console.error('❌ Error completing packing task:', error);
        alert(`Failed to complete packing: ${error.message}`);
        setCompleting(false);
        return;
      }

      console.log('✅ Packing task marked as completed');

      if (extraItems.length > 0) {
        console.log('📦 Adding extra items:', extraItems);
        const { error: extraItemsError } = await supabase
          .from('packing_extra_items')
          .insert(extraItems.map(item => ({ 
            packing_task_id: packingTaskId, 
            order_id: order.id, 
            product_id: item.product_id, 
            quantity: item.quantity 
          })));

        if (extraItemsError) {
          console.error('❌ Error adding extra items:', extraItemsError);
        }

        const { error: orderUpdateError } = await supabase
          .from('orders')
          .update({ has_extra_items: true })
          .eq('id', order.id);

        if (orderUpdateError) {
          console.error('❌ Error updating order:', orderUpdateError);
        }
      }

      console.log('🎉 Packing completed successfully! Moving to completion screen...');
      onComplete(order, { id: packingTaskId });
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      alert(`Unexpected error: ${err.message}`);
      setCompleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 pb-24">
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
              <Package size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Packing Order</h1>
              <p className="text-sm text-gray-600">Pick #{pickingTask.pick_number} • Order #{order.order_number}</p>
              {order.customer && <p className="text-xs text-gray-500">{order.customer.name} • {order.customer.phone}</p>}
            </div>
          </div>
          
          {/* Plastic Bags Alert */}
          {order.plastic_bags_count > 0 && (
            <div className="mt-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3 flex items-center gap-3">
              <div className="bg-yellow-400 rounded-full p-2">
                <ShoppingBag size={20} className="text-yellow-800" />
              </div>
              <div>
                <p className="font-bold text-yellow-800">Plastic Bags Required</p>
                <p className="text-yellow-700 text-lg font-bold">{order.plastic_bags_count} bag{order.plastic_bags_count > 1 ? 's' : ''}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto">
        {/* Shortage Info Banner */}
        {shortageItems.length > 0 && itemsToPack.length > 0 && (
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertTriangle size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">This order has {shortageItems.length} item{shortageItems.length > 1 ? 's' : ''} with shortages</p>
              <p className="text-sm text-amber-700">Only pack the available items below. Shortages are shown at the bottom for reference.</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Scan size={24} className="text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">Scan Items</h2>
          </div>
          <form onSubmit={handleBarcodeSubmit}>
            <input 
              ref={barcodeInputRef} 
              type="text" 
              inputMode="numeric" 
              value={barcode} 
              onChange={(e) => setBarcode(e.target.value)} 
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleBarcodeSubmit(e);
                }
              }}
              placeholder="Scan barcode..." 
              className="w-full px-4 py-4 border-2 border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-600 bg-blue-50 text-lg" 
              autoFocus 
              autoComplete="off" 
            />
          </form>
        </div>

        {/* Show message if no items to pack (all items were out of stock) */}
        {itemsToPack.length === 0 && (
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-4 text-center">
            <AlertTriangle size={48} className="mx-auto text-orange-500 mb-3" />
            <h3 className="font-bold text-orange-800 text-lg mb-2">No Items to Pack</h3>
            <p className="text-orange-600 mb-2">All items in this order were reported as shortage by the picker.</p>
            <p className="text-green-600 font-medium">You can complete this order - click the button below.</p>
          </div>
        )}

        {itemsToPack.map((item) => {
          const itemStatus = getItemStatus(item);
          const isComplete = itemStatus.verifiedQuantity >= itemStatus.expectedQuantity;
          const needsMore = itemStatus.verifiedQuantity < itemStatus.expectedQuantity;
          return (
            <div key={item.id} className={`bg-white rounded-xl shadow-md p-4 mb-4 border-2 ${isComplete ? 'border-green-500' : needsMore && itemStatus.verifiedQuantity > 0 ? 'border-purple-500' : 'border-gray-200'}`}>
              <div className="flex items-start gap-4">
                <img src={getImageUrl(item.products.image) || '/placeholder.png'} alt={item.products.name} className="w-20 h-20 object-cover rounded-lg" />
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-lg">{item.products.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">Barcode: {item.products.barcode}</p>
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-bold">
                      <span className={itemStatus.verifiedQuantity >= itemStatus.expectedQuantity ? 'text-green-600' : 'text-blue-600'}>{itemStatus.verifiedQuantity}</span>
                      <span className="text-gray-400"> / </span>
                      <span className="text-gray-600">{itemStatus.expectedQuantity}</span>
                    </div>
                    {isComplete && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle size={18} />
                        <span className="text-sm font-medium">Verified</span>
                      </div>
                    )}
                    {needsMore && itemStatus.verifiedQuantity > 0 && (
                      <div className="flex items-center gap-1 text-purple-600">
                        <Clock size={18} />
                        <span className="text-sm font-medium">In Progress</span>
                      </div>
                    )}
                  </div>
                  {itemStatus.hasShortage && (
                    <div className="mt-2 flex items-center gap-1 text-sm text-orange-600">
                      <AlertTriangle size={16} />
                      <span>Shortage: {itemStatus.expectedQuantity} of {itemStatus.orderedQuantity} picked</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {extraItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <AlertTriangle size={20} className="text-orange-600" />
              Extra Items Added
            </h3>
            <div className="space-y-3">
              {extraItems.map((item, index) => (
                <div key={index} className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <ShoppingBag size={24} className="text-orange-600" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{item.product_name}</h4>
                      <p className="text-sm text-gray-600">Barcode: {item.barcode}</p>
                      <p className="text-sm text-orange-600">Quantity: {item.quantity}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shortage Items - Information Only (Not Packable) */}
        {shortageItems.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-gray-500 mb-3 flex items-center gap-2">
              <AlertTriangle size={20} className="text-red-400" />
              Not Available (Shortage)
            </h3>
            <p className="text-sm text-gray-500 mb-3">These items were not picked due to shortage. No action required.</p>
            <div className="space-y-3">
              {shortageItems.map((item) => (
                <div key={item.id} className="bg-gray-100 border-2 border-gray-300 rounded-xl p-4 opacity-60">
                  <div className="flex items-start gap-4">
                    <img src={getImageUrl(item.products.image) || '/placeholder.png'} alt={item.products.name} className="w-16 h-16 object-cover rounded-lg grayscale" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-500">{item.products.name}</h4>
                      <p className="text-sm text-gray-400">Barcode: {item.products.barcode}</p>
                      <div className="mt-1 flex items-center gap-1 text-sm text-red-400">
                        <AlertTriangle size={14} />
                        <span>0 of {item.quantity} picked - Out of stock</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <button 
          onClick={handleComplete} 
          disabled={!allItemsVerified() || completing} 
          className={`w-full py-4 rounded-lg font-bold text-white transition-colors ${
            allItemsVerified() && !completing 
              ? 'bg-green-600 hover:bg-green-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {completing ? 'Completing...' : allItemsVerified() ? (shortageItems.length > 0 ? 'Complete Packing (with shortages)' : 'Complete Packing') : 'Verify All Items to Continue'}
        </button>
      </div>

      {showExtraItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={32} className="text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Extra Item Detected</h2>
              <p className="text-gray-600">This item is not in the original order. Do you want to add it?</p>
            </div>
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-orange-800"><strong>Warning:</strong> Adding this item will flag this order for review by the admin.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowExtraItemModal(false); setScannedExtraBarcode(''); setBarcode(''); }} className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleAddExtraItem} className="flex-1 px-4 py-3 bg-orange-600 text-white font-bold rounded-lg hover:bg-orange-700 transition-colors">
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPLETION SCREEN
// ============================================
const CompletionScreen = ({ order, packingTask, onContinue }) => {
  const [showBoxModal, setShowBoxModal] = useState(false);
  const [boxAllocated, setBoxAllocated] = useState(false);
  const [boxConfirmed, setBoxConfirmed] = useState(false);
  const [allocatedBox, setAllocatedBox] = useState(null);
  const [loadingBoxInfo, setLoadingBoxInfo] = useState(true);

  useEffect(() => {
    const loadBoxStatus = async () => {
      setLoadingBoxInfo(true);
      
      if (order?.box_allocation_id) {
        setBoxAllocated(true);
        
        // Check if placement is confirmed
        if (order?.box_placement_confirmed) {
          setBoxConfirmed(true);
        } else {
          // Box allocated but not confirmed - need to show confirmation modal
          console.log('📦 Box allocated but placement not confirmed, showing modal');
          
          // Fetch box details to show in modal
          const { data: allocation } = await supabase
            .from('box_allocations')
            .select('*, delivery_boxes(*)')
            .eq('id', order.box_allocation_id)
            .single();
          
          if (allocation?.delivery_boxes) {
            setAllocatedBox(allocation.delivery_boxes);
          }
          
          // Automatically show the modal to confirm placement
          setShowBoxModal(true);
        }
      }
      
      setLoadingBoxInfo(false);
    };
    
    loadBoxStatus();
  }, [order]);

  const handleBoxAllocated = async (allocation, box) => {
    setBoxAllocated(true);
    setBoxConfirmed(true);
    setAllocatedBox(box);
    setShowBoxModal(false);
    
    // 🔕 Drivers are NOT notified from here — deliberately.
    //
    // Drivers were getting THREE pushes per order. One ready_for_dispatch event had
    // three broadcasters: this call, a trigger_notify_drivers_on_history DB trigger,
    // and send-notification's own notifyDrivers(). Each read the zone from a different
    // place, so the same order arrived as "- Unknown", "" and "- Laudium".
    //
    // The order reaching ready_for_dispatch already fires send-notification, which
    // broadcasts with the CORRECT zone (orders.delivery_zone, maintained by
    // trigger_auto_detect_zone). This call fired at box-confirm, when the zone may not
    // be set yet, and fell back to 'Unknown'. So send-notification is the single sender;
    // the DB trigger is dropped and this call is gone.
  };

  if (loadingBoxInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={40} className="text-green-600" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Packing Complete!</h1>
        {!boxAllocated ? (
          <>
            <p className="text-gray-600 mb-8">Now allocate this order to a delivery box</p>
            <button onClick={() => setShowBoxModal(true)} className="w-full bg-purple-600 text-white font-bold py-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 mb-4">
              <Box size={24} />
              Allocate to Delivery Box
            </button>
            <button onClick={onContinue} className="w-full bg-gray-200 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-300 transition-colors">
              Skip for Now
            </button>
          </>
        ) : !boxConfirmed ? (
          <>
            <p className="text-gray-600 mb-8">Please confirm box placement in the correct bay</p>
            <button onClick={() => setShowBoxModal(true)} className="w-full bg-yellow-500 text-white font-bold py-4 rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2 mb-4">
              <Box size={24} />
              Confirm Box Placement
            </button>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-2">Order allocated to delivery box</p>
            {allocatedBox && (
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-8">
                <div className="flex items-center justify-center gap-2 text-purple-700">
                  <Box size={24} />
                  <span className="text-2xl font-bold">Box {allocatedBox.box_number}</span>
                </div>
              </div>
            )}
            <button onClick={onContinue} className="w-full bg-green-600 text-white font-bold py-4 rounded-lg hover:bg-green-700 transition-colors">
              Pack Next Order
            </button>
          </>
        )}
      </div>
      {showBoxModal && <BoxAllocationModal order={order} packingTask={packingTask} onClose={() => setShowBoxModal(false)} onAllocated={handleBoxAllocated} />}
    </div>
  );
};

// ============================================
// MAIN PACKER APP
// ============================================
const PackerApp = () => {
  const { profile, signOut, loading, isAuthenticated, user } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('scan');
  const [selectedPickingTask, setSelectedPickingTask] = useState(null);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [completedPackingTask, setCompletedPackingTask] = useState(null);
  const [resetKey, setResetKey] = useState(0);

  const handleOrderLoaded = (pickingTask) => {
    setSelectedPickingTask(pickingTask);
    setCurrentScreen('packing');
  };

  const handleResumeCompletion = (order, packingTask) => {
    console.log('📦 Resuming to completion screen for box allocation');
    setCompletedOrder(order);
    setCompletedPackingTask(packingTask);
    setCurrentScreen('completion');
  };

  const handleCompletePacking = (order, packingTask) => {
    setCompletedOrder(order);
    setCompletedPackingTask(packingTask);
    setCurrentScreen('completion');
  };

  const handleContinue = () => {
    console.log('🔄 Returning to scan screen - resetting pick numbers');
    setSelectedPickingTask(null);
    setCompletedOrder(null);
    setCompletedPackingTask(null);
    setResetKey(prev => prev + 1);
    setCurrentScreen('scan');
  };

  const handleLogout = async () => {
    await signOut();
  };

  // Show loading screen while checking auth
  if (loading) {
    return <LoadingScreen />;
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  const packerName = profile?.name || user?.email?.split('@')[0] || 'Packer';

  switch (currentScreen) {
    case 'packing':
      return <PackingScreen pickingTask={selectedPickingTask} onComplete={handleCompletePacking} packerId={user.id} />;
    case 'completion':
      return <CompletionScreen order={completedOrder} packingTask={completedPackingTask} onContinue={handleContinue} />;
    default:
      return <ScanPickNumberScreen onOrderLoaded={handleOrderLoaded} onResumeCompletion={handleResumeCompletion} onLogout={handleLogout} packerName={packerName} resetKey={resetKey} packerId={user.id} />;
  }
};

// ============================================
// APP WRAPPER WITH AUTH
// ============================================
function App() {
  return (
    <AuthProvider>
      <PackerApp />
    </AuthProvider>
  );
}

export default App;
